import { BinaryReader, BinaryWriter } from "@bufbuild/protobuf/wire";
import { Asset_AssetType } from "../content/asset";
import { MediaReference } from "../content/media_reference";
import { CaptureCharacteristics } from "./capture_characteristics";
import { AudioCodecFormat, ImageCodecFormat, VideoCodecFormat } from "./media_codec_formats";
import { VideoFileFormat } from "./media_file_formats";
import { MediaId } from "./media_id";
export const protobufPackage = "snapchat.snapdoc";
export interface AudioMetadata {
    codecFormat: AudioCodecFormat;
}
export interface ImageMetadata {
    codecFormat: ImageCodecFormat;
}
export interface VideoMetadata {
    codecFormat: VideoCodecFormat;
    containerFormat: VideoFileFormat;
}
export interface MediaMetadata {
    orientation: MediaMetadata_Orientation;
    dimensions: MediaMetadata_MediaDimensions | undefined;
    mediaDurationMs: number;
    mediaId: MediaId | undefined;
    encryptionInfoV1: MediaMetadata_MediaEncryptionInfo | undefined;
    encryptionInfoV2: MediaMetadata_MediaEncryptionInfo | undefined;
    masterKeyEncryptedEncryptionInfo: MediaMetadata_MediaEncryptionInfo | undefined;
    assetType: Asset_AssetType;
    captureCharacteristics: CaptureCharacteristics | undefined;
    mediaOriginCamera?: MediaOriginCamera | undefined;
    mediaOriginExternalStorage?: MediaOriginExternalStorage | undefined;
    mediaOriginPixy?: MediaOriginPixy | undefined;
    mediaOriginSpectacles?: MediaOriginSpectacles | undefined;
    mediaOriginAi?: MediaOriginAI | undefined;
    dreamsMetadata?: DreamsMetadata | undefined;
    additionalOrigins: MediaOrigin[];
    displayOrientation: MediaMetadata_DisplayOrientation;
    audio: AudioMetadata | undefined;
    image: ImageMetadata | undefined;
    video: VideoMetadata | undefined;
    type: MediaMetadata_MediaType;
    legacyMediaSource?: MediaMetadata_LegacyMediaSource | undefined;
    contentDescriptor?: Uint8Array | undefined;
    mediaUrl: string;
    hasSound: boolean;
    zipped: boolean;
    frontFacing: boolean;
    mediaReference: MediaReference | undefined;
}
export enum MediaMetadata_Orientation {
    VERTICAL = 0,
    HORIZONTAL = 1,
    UNRECOGNIZED = -1
}
export enum MediaMetadata_DisplayOrientation {
    UNSET = 0,
    UP = 1,
    DOWN = 2,
    LEFT = 3,
    RIGHT = 4,
    UP_MIRRORED = 5,
    DOWN_MIRRORED = 6,
    LEFT_MIRRORED = 7,
    RIGHT_MIRRORED = 8,
    UNRECOGNIZED = -1
}
export enum MediaMetadata_MediaType {
    IMAGE = 0,
    VIDEO = 1,
    GIF = 2,
    AUDIO = 3,
    UNKNOWN = 4,
    UNRECOGNIZED = -1
}
export interface MediaMetadata_MediaDimensions {
    width: number;
    height: number;
}
export interface MediaMetadata_MediaEncryptionInfo {
    key: Uint8Array;
    iv: Uint8Array;
}
export interface MediaMetadata_LegacyMediaSource {
    sourceMediaId: string;
    directDownloadUrl: MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl | undefined;
}
export interface MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl {
    url: string;
    expirySeconds: string;
    type: MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl_MediaUrlType;
    region: string;
}
export enum MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl_MediaUrlType {
    GCS = 0,
    S3 = 1,
    UNRECOGNIZED_VALUE = 2,
    UNRECOGNIZED = -1
}
export interface MediaOrigin {
    mediaOriginCamera?: MediaOriginCamera | undefined;
    mediaOriginExternalStorage?: MediaOriginExternalStorage | undefined;
    mediaOriginPixy?: MediaOriginPixy | undefined;
    mediaOriginSpectacles?: MediaOriginSpectacles | undefined;
    mediaOriginAi?: MediaOriginAI | undefined;
    dreamsMetadata?: DreamsMetadata | undefined;
}
export interface MediaOriginCamera {
}
export interface MediaOriginPixy {
}
export interface MediaOriginSpectacles {
}
export interface MediaOriginExternalStorage {
    creationTimestamp: string;
    mediaSource: MediaOriginExternalStorage_ExternalMediaSource;
    insecureFileMetadata: InsecureFileMetadata | undefined;
    originalLocalIds: string[];
}
export enum MediaOriginExternalStorage_ExternalMediaSource {
    UNSET = 0,
    NONE = 1,
    UNKNOWN = 2,
    SNAPCHAT = 3,
    TIKTOK = 4,
    INSTAGRAM = 5,
    CAPCUT = 6,
    UNRECOGNIZED = -1
}
export interface InsecureFileMetadata {
    lensId: string[];
    musicTrackId: string;
}
export interface MediaOriginAI {
}
export interface DreamsMetadata {
    packId: string;
    templateId: string;
    contentType: DreamsMetadata_LensContentType[];
}
export enum DreamsMetadata_LensContentType {
    UNSET = 0,
    NONE = 1,
    GEN_ML = 2,
    UCO = 3,
    UNRECOGNIZED = -1
}
function createBaseAudioMetadata(): AudioMetadata {
    return { codecFormat: 0 };
}
export const AudioMetadata: MessageFns<AudioMetadata> = {
    encode(message: AudioMetadata, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.codecFormat !== 0) {
            writer.uint32(8).int32(message.codecFormat);
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): AudioMetadata {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseAudioMetadata();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 8) {
                        break;
                    }
                    message.codecFormat = reader.int32() as any;
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<AudioMetadata>, I>>(base?: I): AudioMetadata {
        return AudioMetadata.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<AudioMetadata>, I>>(object: I): AudioMetadata {
        const message = createBaseAudioMetadata();
        message.codecFormat = object.codecFormat ?? 0;
        return message;
    },
};
function createBaseImageMetadata(): ImageMetadata {
    return { codecFormat: 0 };
}
export const ImageMetadata: MessageFns<ImageMetadata> = {
    encode(message: ImageMetadata, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.codecFormat !== 0) {
            writer.uint32(8).int32(message.codecFormat);
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): ImageMetadata {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseImageMetadata();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 8) {
                        break;
                    }
                    message.codecFormat = reader.int32() as any;
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<ImageMetadata>, I>>(base?: I): ImageMetadata {
        return ImageMetadata.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<ImageMetadata>, I>>(object: I): ImageMetadata {
        const message = createBaseImageMetadata();
        message.codecFormat = object.codecFormat ?? 0;
        return message;
    },
};
function createBaseVideoMetadata(): VideoMetadata {
    return { codecFormat: 0, containerFormat: 0 };
}
export const VideoMetadata: MessageFns<VideoMetadata> = {
    encode(message: VideoMetadata, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.codecFormat !== 0) {
            writer.uint32(8).int32(message.codecFormat);
        }
        if (message.containerFormat !== 0) {
            writer.uint32(16).int32(message.containerFormat);
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): VideoMetadata {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseVideoMetadata();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 8) {
                        break;
                    }
                    message.codecFormat = reader.int32() as any;
                    continue;
                }
                case 2: {
                    if (tag !== 16) {
                        break;
                    }
                    message.containerFormat = reader.int32() as any;
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<VideoMetadata>, I>>(base?: I): VideoMetadata {
        return VideoMetadata.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<VideoMetadata>, I>>(object: I): VideoMetadata {
        const message = createBaseVideoMetadata();
        message.codecFormat = object.codecFormat ?? 0;
        message.containerFormat = object.containerFormat ?? 0;
        return message;
    },
};
function createBaseMediaMetadata(): MediaMetadata {
    return {
        orientation: 0,
        dimensions: undefined,
        mediaDurationMs: 0,
        mediaId: undefined,
        encryptionInfoV1: undefined,
        encryptionInfoV2: undefined,
        masterKeyEncryptedEncryptionInfo: undefined,
        assetType: 0,
        captureCharacteristics: undefined,
        mediaOriginCamera: undefined,
        mediaOriginExternalStorage: undefined,
        mediaOriginPixy: undefined,
        mediaOriginSpectacles: undefined,
        mediaOriginAi: undefined,
        dreamsMetadata: undefined,
        additionalOrigins: [],
        displayOrientation: 0,
        audio: undefined,
        image: undefined,
        video: undefined,
        type: 0,
        legacyMediaSource: undefined,
        contentDescriptor: undefined,
        mediaUrl: "",
        hasSound: false,
        zipped: false,
        frontFacing: false,
        mediaReference: undefined,
    };
}
export const MediaMetadata: MessageFns<MediaMetadata> = {
    encode(message: MediaMetadata, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.orientation !== 0) {
            writer.uint32(24).int32(message.orientation);
        }
        if (message.dimensions !== undefined) {
            MediaMetadata_MediaDimensions.encode(message.dimensions, writer.uint32(42).fork()).join();
        }
        if (message.mediaDurationMs !== 0) {
            writer.uint32(120).uint32(message.mediaDurationMs);
        }
        if (message.mediaId !== undefined) {
            MediaId.encode(message.mediaId, writer.uint32(146).fork()).join();
        }
        if (message.encryptionInfoV1 !== undefined) {
            MediaMetadata_MediaEncryptionInfo.encode(message.encryptionInfoV1, writer.uint32(34).fork()).join();
        }
        if (message.encryptionInfoV2 !== undefined) {
            MediaMetadata_MediaEncryptionInfo.encode(message.encryptionInfoV2, writer.uint32(154).fork()).join();
        }
        if (message.masterKeyEncryptedEncryptionInfo !== undefined) {
            MediaMetadata_MediaEncryptionInfo.encode(message.masterKeyEncryptedEncryptionInfo, writer.uint32(250).fork())
                .join();
        }
        if (message.assetType !== 0) {
            writer.uint32(160).int32(message.assetType);
        }
        if (message.captureCharacteristics !== undefined) {
            CaptureCharacteristics.encode(message.captureCharacteristics, writer.uint32(170).fork()).join();
        }
        if (message.mediaOriginCamera !== undefined) {
            MediaOriginCamera.encode(message.mediaOriginCamera, writer.uint32(210).fork()).join();
        }
        if (message.mediaOriginExternalStorage !== undefined) {
            MediaOriginExternalStorage.encode(message.mediaOriginExternalStorage, writer.uint32(218).fork()).join();
        }
        if (message.mediaOriginPixy !== undefined) {
            MediaOriginPixy.encode(message.mediaOriginPixy, writer.uint32(226).fork()).join();
        }
        if (message.mediaOriginSpectacles !== undefined) {
            MediaOriginSpectacles.encode(message.mediaOriginSpectacles, writer.uint32(234).fork()).join();
        }
        if (message.mediaOriginAi !== undefined) {
            MediaOriginAI.encode(message.mediaOriginAi, writer.uint32(242).fork()).join();
        }
        if (message.dreamsMetadata !== undefined) {
            DreamsMetadata.encode(message.dreamsMetadata, writer.uint32(258).fork()).join();
        }
        for (const v of message.additionalOrigins) {
            MediaOrigin.encode(v!, writer.uint32(266).fork()).join();
        }
        if (message.displayOrientation !== 0) {
            writer.uint32(176).int32(message.displayOrientation);
        }
        if (message.audio !== undefined) {
            AudioMetadata.encode(message.audio, writer.uint32(186).fork()).join();
        }
        if (message.image !== undefined) {
            ImageMetadata.encode(message.image, writer.uint32(194).fork()).join();
        }
        if (message.video !== undefined) {
            VideoMetadata.encode(message.video, writer.uint32(202).fork()).join();
        }
        if (message.type !== 0) {
            writer.uint32(16).int32(message.type);
        }
        if (message.legacyMediaSource !== undefined) {
            MediaMetadata_LegacyMediaSource.encode(message.legacyMediaSource, writer.uint32(50).fork()).join();
        }
        if (message.contentDescriptor !== undefined) {
            writer.uint32(58).bytes(message.contentDescriptor);
        }
        if (message.mediaUrl !== "") {
            writer.uint32(74).string(message.mediaUrl);
        }
        if (message.hasSound !== false) {
            writer.uint32(96).bool(message.hasSound);
        }
        if (message.zipped !== false) {
            writer.uint32(104).bool(message.zipped);
        }
        if (message.frontFacing !== false) {
            writer.uint32(112).bool(message.frontFacing);
        }
        if (message.mediaReference !== undefined) {
            MediaReference.encode(message.mediaReference, writer.uint32(138).fork()).join();
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): MediaMetadata {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseMediaMetadata();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 3: {
                    if (tag !== 24) {
                        break;
                    }
                    message.orientation = reader.int32() as any;
                    continue;
                }
                case 5: {
                    if (tag !== 42) {
                        break;
                    }
                    message.dimensions = MediaMetadata_MediaDimensions.decode(reader, reader.uint32());
                    continue;
                }
                case 15: {
                    if (tag !== 120) {
                        break;
                    }
                    message.mediaDurationMs = reader.uint32();
                    continue;
                }
                case 18: {
                    if (tag !== 146) {
                        break;
                    }
                    message.mediaId = MediaId.decode(reader, reader.uint32());
                    continue;
                }
                case 4: {
                    if (tag !== 34) {
                        break;
                    }
                    message.encryptionInfoV1 = MediaMetadata_MediaEncryptionInfo.decode(reader, reader.uint32());
                    continue;
                }
                case 19: {
                    if (tag !== 154) {
                        break;
                    }
                    message.encryptionInfoV2 = MediaMetadata_MediaEncryptionInfo.decode(reader, reader.uint32());
                    continue;
                }
                case 31: {
                    if (tag !== 250) {
                        break;
                    }
                    message.masterKeyEncryptedEncryptionInfo = MediaMetadata_MediaEncryptionInfo.decode(reader, reader.uint32());
                    continue;
                }
                case 20: {
                    if (tag !== 160) {
                        break;
                    }
                    message.assetType = reader.int32() as any;
                    continue;
                }
                case 21: {
                    if (tag !== 170) {
                        break;
                    }
                    message.captureCharacteristics = CaptureCharacteristics.decode(reader, reader.uint32());
                    continue;
                }
                case 26: {
                    if (tag !== 210) {
                        break;
                    }
                    message.mediaOriginCamera = MediaOriginCamera.decode(reader, reader.uint32());
                    continue;
                }
                case 27: {
                    if (tag !== 218) {
                        break;
                    }
                    message.mediaOriginExternalStorage = MediaOriginExternalStorage.decode(reader, reader.uint32());
                    continue;
                }
                case 28: {
                    if (tag !== 226) {
                        break;
                    }
                    message.mediaOriginPixy = MediaOriginPixy.decode(reader, reader.uint32());
                    continue;
                }
                case 29: {
                    if (tag !== 234) {
                        break;
                    }
                    message.mediaOriginSpectacles = MediaOriginSpectacles.decode(reader, reader.uint32());
                    continue;
                }
                case 30: {
                    if (tag !== 242) {
                        break;
                    }
                    message.mediaOriginAi = MediaOriginAI.decode(reader, reader.uint32());
                    continue;
                }
                case 32: {
                    if (tag !== 258) {
                        break;
                    }
                    message.dreamsMetadata = DreamsMetadata.decode(reader, reader.uint32());
                    continue;
                }
                case 33: {
                    if (tag !== 266) {
                        break;
                    }
                    message.additionalOrigins.push(MediaOrigin.decode(reader, reader.uint32()));
                    continue;
                }
                case 22: {
                    if (tag !== 176) {
                        break;
                    }
                    message.displayOrientation = reader.int32() as any;
                    continue;
                }
                case 23: {
                    if (tag !== 186) {
                        break;
                    }
                    message.audio = AudioMetadata.decode(reader, reader.uint32());
                    continue;
                }
                case 24: {
                    if (tag !== 194) {
                        break;
                    }
                    message.image = ImageMetadata.decode(reader, reader.uint32());
                    continue;
                }
                case 25: {
                    if (tag !== 202) {
                        break;
                    }
                    message.video = VideoMetadata.decode(reader, reader.uint32());
                    continue;
                }
                case 2: {
                    if (tag !== 16) {
                        break;
                    }
                    message.type = reader.int32() as any;
                    continue;
                }
                case 6: {
                    if (tag !== 50) {
                        break;
                    }
                    message.legacyMediaSource = MediaMetadata_LegacyMediaSource.decode(reader, reader.uint32());
                    continue;
                }
                case 7: {
                    if (tag !== 58) {
                        break;
                    }
                    message.contentDescriptor = reader.bytes();
                    continue;
                }
                case 9: {
                    if (tag !== 74) {
                        break;
                    }
                    message.mediaUrl = reader.string();
                    continue;
                }
                case 12: {
                    if (tag !== 96) {
                        break;
                    }
                    message.hasSound = reader.bool();
                    continue;
                }
                case 13: {
                    if (tag !== 104) {
                        break;
                    }
                    message.zipped = reader.bool();
                    continue;
                }
                case 14: {
                    if (tag !== 112) {
                        break;
                    }
                    message.frontFacing = reader.bool();
                    continue;
                }
                case 17: {
                    if (tag !== 138) {
                        break;
                    }
                    message.mediaReference = MediaReference.decode(reader, reader.uint32());
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<MediaMetadata>, I>>(base?: I): MediaMetadata {
        return MediaMetadata.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<MediaMetadata>, I>>(object: I): MediaMetadata {
        const message = createBaseMediaMetadata();
        message.orientation = object.orientation ?? 0;
        message.dimensions = (object.dimensions !== undefined && object.dimensions !== null)
            ? MediaMetadata_MediaDimensions.fromPartial(object.dimensions)
            : undefined;
        message.mediaDurationMs = object.mediaDurationMs ?? 0;
        message.mediaId = (object.mediaId !== undefined && object.mediaId !== null)
            ? MediaId.fromPartial(object.mediaId)
            : undefined;
        message.encryptionInfoV1 = (object.encryptionInfoV1 !== undefined && object.encryptionInfoV1 !== null)
            ? MediaMetadata_MediaEncryptionInfo.fromPartial(object.encryptionInfoV1)
            : undefined;
        message.encryptionInfoV2 = (object.encryptionInfoV2 !== undefined && object.encryptionInfoV2 !== null)
            ? MediaMetadata_MediaEncryptionInfo.fromPartial(object.encryptionInfoV2)
            : undefined;
        message.masterKeyEncryptedEncryptionInfo =
            (object.masterKeyEncryptedEncryptionInfo !== undefined && object.masterKeyEncryptedEncryptionInfo !== null)
                ? MediaMetadata_MediaEncryptionInfo.fromPartial(object.masterKeyEncryptedEncryptionInfo)
                : undefined;
        message.assetType = object.assetType ?? 0;
        message.captureCharacteristics =
            (object.captureCharacteristics !== undefined && object.captureCharacteristics !== null)
                ? CaptureCharacteristics.fromPartial(object.captureCharacteristics)
                : undefined;
        message.mediaOriginCamera = (object.mediaOriginCamera !== undefined && object.mediaOriginCamera !== null)
            ? MediaOriginCamera.fromPartial(object.mediaOriginCamera)
            : undefined;
        message.mediaOriginExternalStorage =
            (object.mediaOriginExternalStorage !== undefined && object.mediaOriginExternalStorage !== null)
                ? MediaOriginExternalStorage.fromPartial(object.mediaOriginExternalStorage)
                : undefined;
        message.mediaOriginPixy = (object.mediaOriginPixy !== undefined && object.mediaOriginPixy !== null)
            ? MediaOriginPixy.fromPartial(object.mediaOriginPixy)
            : undefined;
        message.mediaOriginSpectacles =
            (object.mediaOriginSpectacles !== undefined && object.mediaOriginSpectacles !== null)
                ? MediaOriginSpectacles.fromPartial(object.mediaOriginSpectacles)
                : undefined;
        message.mediaOriginAi = (object.mediaOriginAi !== undefined && object.mediaOriginAi !== null)
            ? MediaOriginAI.fromPartial(object.mediaOriginAi)
            : undefined;
        message.dreamsMetadata = (object.dreamsMetadata !== undefined && object.dreamsMetadata !== null)
            ? DreamsMetadata.fromPartial(object.dreamsMetadata)
            : undefined;
        message.additionalOrigins = object.additionalOrigins?.map((e) => MediaOrigin.fromPartial(e)) || [];
        message.displayOrientation = object.displayOrientation ?? 0;
        message.audio = (object.audio !== undefined && object.audio !== null)
            ? AudioMetadata.fromPartial(object.audio)
            : undefined;
        message.image = (object.image !== undefined && object.image !== null)
            ? ImageMetadata.fromPartial(object.image)
            : undefined;
        message.video = (object.video !== undefined && object.video !== null)
            ? VideoMetadata.fromPartial(object.video)
            : undefined;
        message.type = object.type ?? 0;
        message.legacyMediaSource = (object.legacyMediaSource !== undefined && object.legacyMediaSource !== null)
            ? MediaMetadata_LegacyMediaSource.fromPartial(object.legacyMediaSource)
            : undefined;
        message.contentDescriptor = object.contentDescriptor ?? undefined;
        message.mediaUrl = object.mediaUrl ?? "";
        message.hasSound = object.hasSound ?? false;
        message.zipped = object.zipped ?? false;
        message.frontFacing = object.frontFacing ?? false;
        message.mediaReference = (object.mediaReference !== undefined && object.mediaReference !== null)
            ? MediaReference.fromPartial(object.mediaReference)
            : undefined;
        return message;
    },
};
function createBaseMediaMetadata_MediaDimensions(): MediaMetadata_MediaDimensions {
    return { width: 0, height: 0 };
}
export const MediaMetadata_MediaDimensions: MessageFns<MediaMetadata_MediaDimensions> = {
    encode(message: MediaMetadata_MediaDimensions, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.width !== 0) {
            writer.uint32(8).uint32(message.width);
        }
        if (message.height !== 0) {
            writer.uint32(16).uint32(message.height);
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): MediaMetadata_MediaDimensions {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseMediaMetadata_MediaDimensions();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 8) {
                        break;
                    }
                    message.width = reader.uint32();
                    continue;
                }
                case 2: {
                    if (tag !== 16) {
                        break;
                    }
                    message.height = reader.uint32();
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<MediaMetadata_MediaDimensions>, I>>(base?: I): MediaMetadata_MediaDimensions {
        return MediaMetadata_MediaDimensions.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<MediaMetadata_MediaDimensions>, I>>(object: I): MediaMetadata_MediaDimensions {
        const message = createBaseMediaMetadata_MediaDimensions();
        message.width = object.width ?? 0;
        message.height = object.height ?? 0;
        return message;
    },
};
function createBaseMediaMetadata_MediaEncryptionInfo(): MediaMetadata_MediaEncryptionInfo {
    return { key: new Uint8Array(0), iv: new Uint8Array(0) };
}
export const MediaMetadata_MediaEncryptionInfo: MessageFns<MediaMetadata_MediaEncryptionInfo> = {
    encode(message: MediaMetadata_MediaEncryptionInfo, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.key.length !== 0) {
            writer.uint32(10).bytes(message.key);
        }
        if (message.iv.length !== 0) {
            writer.uint32(18).bytes(message.iv);
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): MediaMetadata_MediaEncryptionInfo {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseMediaMetadata_MediaEncryptionInfo();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 10) {
                        break;
                    }
                    message.key = reader.bytes();
                    continue;
                }
                case 2: {
                    if (tag !== 18) {
                        break;
                    }
                    message.iv = reader.bytes();
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<MediaMetadata_MediaEncryptionInfo>, I>>(base?: I): MediaMetadata_MediaEncryptionInfo {
        return MediaMetadata_MediaEncryptionInfo.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<MediaMetadata_MediaEncryptionInfo>, I>>(object: I): MediaMetadata_MediaEncryptionInfo {
        const message = createBaseMediaMetadata_MediaEncryptionInfo();
        message.key = object.key ?? new Uint8Array(0);
        message.iv = object.iv ?? new Uint8Array(0);
        return message;
    },
};
function createBaseMediaMetadata_LegacyMediaSource(): MediaMetadata_LegacyMediaSource {
    return { sourceMediaId: "", directDownloadUrl: undefined };
}
export const MediaMetadata_LegacyMediaSource: MessageFns<MediaMetadata_LegacyMediaSource> = {
    encode(message: MediaMetadata_LegacyMediaSource, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.sourceMediaId !== "") {
            writer.uint32(10).string(message.sourceMediaId);
        }
        if (message.directDownloadUrl !== undefined) {
            MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl.encode(message.directDownloadUrl, writer.uint32(18).fork()).join();
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): MediaMetadata_LegacyMediaSource {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseMediaMetadata_LegacyMediaSource();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 10) {
                        break;
                    }
                    message.sourceMediaId = reader.string();
                    continue;
                }
                case 2: {
                    if (tag !== 18) {
                        break;
                    }
                    message.directDownloadUrl = MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl.decode(reader, reader.uint32());
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<MediaMetadata_LegacyMediaSource>, I>>(base?: I): MediaMetadata_LegacyMediaSource {
        return MediaMetadata_LegacyMediaSource.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<MediaMetadata_LegacyMediaSource>, I>>(object: I): MediaMetadata_LegacyMediaSource {
        const message = createBaseMediaMetadata_LegacyMediaSource();
        message.sourceMediaId = object.sourceMediaId ?? "";
        message.directDownloadUrl = (object.directDownloadUrl !== undefined && object.directDownloadUrl !== null)
            ? MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl.fromPartial(object.directDownloadUrl)
            : undefined;
        return message;
    },
};
function createBaseMediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl(): MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl {
    return { url: "", expirySeconds: "0", type: 0, region: "" };
}
export const MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl: MessageFns<MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl> = {
    encode(message: MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.url !== "") {
            writer.uint32(10).string(message.url);
        }
        if (message.expirySeconds !== "0") {
            writer.uint32(16).uint64(message.expirySeconds);
        }
        if (message.type !== 0) {
            writer.uint32(24).int32(message.type);
        }
        if (message.region !== "") {
            writer.uint32(34).string(message.region);
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseMediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 10) {
                        break;
                    }
                    message.url = reader.string();
                    continue;
                }
                case 2: {
                    if (tag !== 16) {
                        break;
                    }
                    message.expirySeconds = reader.uint64().toString();
                    continue;
                }
                case 3: {
                    if (tag !== 24) {
                        break;
                    }
                    message.type = reader.int32() as any;
                    continue;
                }
                case 4: {
                    if (tag !== 34) {
                        break;
                    }
                    message.region = reader.string();
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl>, I>>(base?: I): MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl {
        return MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl>, I>>(object: I): MediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl {
        const message = createBaseMediaMetadata_LegacyMediaSource_LegacyDirectDownloadUrl();
        message.url = object.url ?? "";
        message.expirySeconds = object.expirySeconds ?? "0";
        message.type = object.type ?? 0;
        message.region = object.region ?? "";
        return message;
    },
};
function createBaseMediaOrigin(): MediaOrigin {
    return {
        mediaOriginCamera: undefined,
        mediaOriginExternalStorage: undefined,
        mediaOriginPixy: undefined,
        mediaOriginSpectacles: undefined,
        mediaOriginAi: undefined,
        dreamsMetadata: undefined,
    };
}
export const MediaOrigin: MessageFns<MediaOrigin> = {
    encode(message: MediaOrigin, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.mediaOriginCamera !== undefined) {
            MediaOriginCamera.encode(message.mediaOriginCamera, writer.uint32(10).fork()).join();
        }
        if (message.mediaOriginExternalStorage !== undefined) {
            MediaOriginExternalStorage.encode(message.mediaOriginExternalStorage, writer.uint32(18).fork()).join();
        }
        if (message.mediaOriginPixy !== undefined) {
            MediaOriginPixy.encode(message.mediaOriginPixy, writer.uint32(26).fork()).join();
        }
        if (message.mediaOriginSpectacles !== undefined) {
            MediaOriginSpectacles.encode(message.mediaOriginSpectacles, writer.uint32(34).fork()).join();
        }
        if (message.mediaOriginAi !== undefined) {
            MediaOriginAI.encode(message.mediaOriginAi, writer.uint32(42).fork()).join();
        }
        if (message.dreamsMetadata !== undefined) {
            DreamsMetadata.encode(message.dreamsMetadata, writer.uint32(50).fork()).join();
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): MediaOrigin {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseMediaOrigin();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 10) {
                        break;
                    }
                    message.mediaOriginCamera = MediaOriginCamera.decode(reader, reader.uint32());
                    continue;
                }
                case 2: {
                    if (tag !== 18) {
                        break;
                    }
                    message.mediaOriginExternalStorage = MediaOriginExternalStorage.decode(reader, reader.uint32());
                    continue;
                }
                case 3: {
                    if (tag !== 26) {
                        break;
                    }
                    message.mediaOriginPixy = MediaOriginPixy.decode(reader, reader.uint32());
                    continue;
                }
                case 4: {
                    if (tag !== 34) {
                        break;
                    }
                    message.mediaOriginSpectacles = MediaOriginSpectacles.decode(reader, reader.uint32());
                    continue;
                }
                case 5: {
                    if (tag !== 42) {
                        break;
                    }
                    message.mediaOriginAi = MediaOriginAI.decode(reader, reader.uint32());
                    continue;
                }
                case 6: {
                    if (tag !== 50) {
                        break;
                    }
                    message.dreamsMetadata = DreamsMetadata.decode(reader, reader.uint32());
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<MediaOrigin>, I>>(base?: I): MediaOrigin {
        return MediaOrigin.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<MediaOrigin>, I>>(object: I): MediaOrigin {
        const message = createBaseMediaOrigin();
        message.mediaOriginCamera = (object.mediaOriginCamera !== undefined && object.mediaOriginCamera !== null)
            ? MediaOriginCamera.fromPartial(object.mediaOriginCamera)
            : undefined;
        message.mediaOriginExternalStorage =
            (object.mediaOriginExternalStorage !== undefined && object.mediaOriginExternalStorage !== null)
                ? MediaOriginExternalStorage.fromPartial(object.mediaOriginExternalStorage)
                : undefined;
        message.mediaOriginPixy = (object.mediaOriginPixy !== undefined && object.mediaOriginPixy !== null)
            ? MediaOriginPixy.fromPartial(object.mediaOriginPixy)
            : undefined;
        message.mediaOriginSpectacles =
            (object.mediaOriginSpectacles !== undefined && object.mediaOriginSpectacles !== null)
                ? MediaOriginSpectacles.fromPartial(object.mediaOriginSpectacles)
                : undefined;
        message.mediaOriginAi = (object.mediaOriginAi !== undefined && object.mediaOriginAi !== null)
            ? MediaOriginAI.fromPartial(object.mediaOriginAi)
            : undefined;
        message.dreamsMetadata = (object.dreamsMetadata !== undefined && object.dreamsMetadata !== null)
            ? DreamsMetadata.fromPartial(object.dreamsMetadata)
            : undefined;
        return message;
    },
};
function createBaseMediaOriginCamera(): MediaOriginCamera {
    return {};
}
export const MediaOriginCamera: MessageFns<MediaOriginCamera> = {
    encode(_: MediaOriginCamera, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): MediaOriginCamera {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseMediaOriginCamera();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<MediaOriginCamera>, I>>(base?: I): MediaOriginCamera {
        return MediaOriginCamera.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<MediaOriginCamera>, I>>(_: I): MediaOriginCamera {
        const message = createBaseMediaOriginCamera();
        return message;
    },
};
function createBaseMediaOriginPixy(): MediaOriginPixy {
    return {};
}
export const MediaOriginPixy: MessageFns<MediaOriginPixy> = {
    encode(_: MediaOriginPixy, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): MediaOriginPixy {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseMediaOriginPixy();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<MediaOriginPixy>, I>>(base?: I): MediaOriginPixy {
        return MediaOriginPixy.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<MediaOriginPixy>, I>>(_: I): MediaOriginPixy {
        const message = createBaseMediaOriginPixy();
        return message;
    },
};
function createBaseMediaOriginSpectacles(): MediaOriginSpectacles {
    return {};
}
export const MediaOriginSpectacles: MessageFns<MediaOriginSpectacles> = {
    encode(_: MediaOriginSpectacles, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): MediaOriginSpectacles {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseMediaOriginSpectacles();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<MediaOriginSpectacles>, I>>(base?: I): MediaOriginSpectacles {
        return MediaOriginSpectacles.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<MediaOriginSpectacles>, I>>(_: I): MediaOriginSpectacles {
        const message = createBaseMediaOriginSpectacles();
        return message;
    },
};
function createBaseMediaOriginExternalStorage(): MediaOriginExternalStorage {
    return { creationTimestamp: "0", mediaSource: 0, insecureFileMetadata: undefined, originalLocalIds: [] };
}
export const MediaOriginExternalStorage: MessageFns<MediaOriginExternalStorage> = {
    encode(message: MediaOriginExternalStorage, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.creationTimestamp !== "0") {
            writer.uint32(8).uint64(message.creationTimestamp);
        }
        if (message.mediaSource !== 0) {
            writer.uint32(16).int32(message.mediaSource);
        }
        if (message.insecureFileMetadata !== undefined) {
            InsecureFileMetadata.encode(message.insecureFileMetadata, writer.uint32(42).fork()).join();
        }
        for (const v of message.originalLocalIds) {
            writer.uint32(50).string(v!);
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): MediaOriginExternalStorage {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseMediaOriginExternalStorage();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 8) {
                        break;
                    }
                    message.creationTimestamp = reader.uint64().toString();
                    continue;
                }
                case 2: {
                    if (tag !== 16) {
                        break;
                    }
                    message.mediaSource = reader.int32() as any;
                    continue;
                }
                case 5: {
                    if (tag !== 42) {
                        break;
                    }
                    message.insecureFileMetadata = InsecureFileMetadata.decode(reader, reader.uint32());
                    continue;
                }
                case 6: {
                    if (tag !== 50) {
                        break;
                    }
                    message.originalLocalIds.push(reader.string());
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<MediaOriginExternalStorage>, I>>(base?: I): MediaOriginExternalStorage {
        return MediaOriginExternalStorage.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<MediaOriginExternalStorage>, I>>(object: I): MediaOriginExternalStorage {
        const message = createBaseMediaOriginExternalStorage();
        message.creationTimestamp = object.creationTimestamp ?? "0";
        message.mediaSource = object.mediaSource ?? 0;
        message.insecureFileMetadata = (object.insecureFileMetadata !== undefined && object.insecureFileMetadata !== null)
            ? InsecureFileMetadata.fromPartial(object.insecureFileMetadata)
            : undefined;
        message.originalLocalIds = object.originalLocalIds?.map((e) => e) || [];
        return message;
    },
};
function createBaseInsecureFileMetadata(): InsecureFileMetadata {
    return { lensId: [], musicTrackId: "0" };
}
export const InsecureFileMetadata: MessageFns<InsecureFileMetadata> = {
    encode(message: InsecureFileMetadata, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        writer.uint32(10).fork();
        for (const v of message.lensId) {
            writer.uint64(v);
        }
        writer.join();
        if (message.musicTrackId !== "0") {
            writer.uint32(16).uint64(message.musicTrackId);
        }
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): InsecureFileMetadata {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseInsecureFileMetadata();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag === 8) {
                        message.lensId.push(reader.uint64().toString());
                        continue;
                    }
                    if (tag === 10) {
                        const end2 = reader.uint32() + reader.pos;
                        while (reader.pos < end2) {
                            message.lensId.push(reader.uint64().toString());
                        }
                        continue;
                    }
                    break;
                }
                case 2: {
                    if (tag !== 16) {
                        break;
                    }
                    message.musicTrackId = reader.uint64().toString();
                    continue;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<InsecureFileMetadata>, I>>(base?: I): InsecureFileMetadata {
        return InsecureFileMetadata.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<InsecureFileMetadata>, I>>(object: I): InsecureFileMetadata {
        const message = createBaseInsecureFileMetadata();
        message.lensId = object.lensId?.map((e) => e) || [];
        message.musicTrackId = object.musicTrackId ?? "0";
        return message;
    },
};
function createBaseMediaOriginAI(): MediaOriginAI {
    return {};
}
export const MediaOriginAI: MessageFns<MediaOriginAI> = {
    encode(_: MediaOriginAI, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): MediaOriginAI {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseMediaOriginAI();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<MediaOriginAI>, I>>(base?: I): MediaOriginAI {
        return MediaOriginAI.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<MediaOriginAI>, I>>(_: I): MediaOriginAI {
        const message = createBaseMediaOriginAI();
        return message;
    },
};
function createBaseDreamsMetadata(): DreamsMetadata {
    return { packId: "", templateId: "", contentType: [] };
}
export const DreamsMetadata: MessageFns<DreamsMetadata> = {
    encode(message: DreamsMetadata, writer: BinaryWriter = new BinaryWriter()): BinaryWriter {
        if (message.packId !== "") {
            writer.uint32(10).string(message.packId);
        }
        if (message.templateId !== "") {
            writer.uint32(18).string(message.templateId);
        }
        writer.uint32(26).fork();
        for (const v of message.contentType) {
            writer.int32(v);
        }
        writer.join();
        return writer;
    },
    decode(input: BinaryReader | Uint8Array, length?: number): DreamsMetadata {
        const reader = input instanceof BinaryReader ? input : new BinaryReader(input);
        let end = length === undefined ? reader.len : reader.pos + length;
        const message = createBaseDreamsMetadata();
        while (reader.pos < end) {
            const tag = reader.uint32();
            switch (tag >>> 3) {
                case 1: {
                    if (tag !== 10) {
                        break;
                    }
                    message.packId = reader.string();
                    continue;
                }
                case 2: {
                    if (tag !== 18) {
                        break;
                    }
                    message.templateId = reader.string();
                    continue;
                }
                case 3: {
                    if (tag === 24) {
                        message.contentType.push(reader.int32() as any);
                        continue;
                    }
                    if (tag === 26) {
                        const end2 = reader.uint32() + reader.pos;
                        while (reader.pos < end2) {
                            message.contentType.push(reader.int32() as any);
                        }
                        continue;
                    }
                    break;
                }
            }
            if ((tag & 7) === 4 || tag === 0) {
                break;
            }
            reader.skip(tag & 7);
        }
        return message;
    },
    create<I extends Exact<DeepPartial<DreamsMetadata>, I>>(base?: I): DreamsMetadata {
        return DreamsMetadata.fromPartial(base ?? ({} as any));
    },
    fromPartial<I extends Exact<DeepPartial<DreamsMetadata>, I>>(object: I): DreamsMetadata {
        const message = createBaseDreamsMetadata();
        message.packId = object.packId ?? "";
        message.templateId = object.templateId ?? "";
        message.contentType = object.contentType?.map((e) => e) || [];
        return message;
    },
};
type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;
export type DeepPartial<T> = T extends Builtin ? T : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>> : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>> : T extends {} ? {
    [K in keyof T]?: DeepPartial<T[K]>;
} : Partial<T>;
type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P : P & {
    [K in keyof P]: Exact<P[K], I[K]>;
} & {
    [K in Exclude<keyof I, KeysOfUnion<P>>]: never;
};
export interface MessageFns<T> {
    encode(message: T, writer?: BinaryWriter): BinaryWriter;
    decode(input: BinaryReader | Uint8Array, length?: number): T;
    create<I extends Exact<DeepPartial<T>, I>>(base?: I): T;
    fromPartial<I extends Exact<DeepPartial<T>, I>>(object: I): T;
}
